import { useEffect, useState } from "react";
import { Tag, FloppyDisk, Eye, EyeSlash, ArrowClockwise, Check, Prohibit, PencilSimple } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

/**
 * Admin · Product Tags panel — currently manages the "OG Legacy" collection
 * (products with the branded OG Sweat for Sacrifice designs). Products
 * appear in their normal categories AND in this dedicated section on /shop.
 *
 * Any product added here also shows up in the "OG Legacy" collection section
 * on the top of /shop (between Featured Bundles and the Live Catalog).
 */
export default function ProductTagsPanel({ token, backend }) {
  const [tags, setTags] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState(null); // tag_name being picked
  const [drafts, setDrafts] = useState({}); // tag_name -> { label, heading, description }

  const flash = (s) => { setMsg(s); setTimeout(() => setMsg(""), 2200); };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [tr, pr] = await Promise.all([
        fetch(`${backend}/api/admin/product-tags`, { headers: { "X-Admin-Token": token } }),
        fetch(`${backend}/api/admin/printful-products`, { headers: { "X-Admin-Token": token } }),
      ]);
      if (!tr.ok) throw new Error(`admin HTTP ${tr.status}`);
      const td = await tr.json();
      setTags(td.tags || []);
      if (pr.ok) {
        const pd = await pr.json();
        setAllProducts(pd.products || []);
      } else {
        // Fallback to public shop list
        const fb = await fetch(`${backend}/api/shop/products`);
        if (fb.ok) { const fd = await fb.json(); setAllProducts(fd.products || []); }
      }
    } catch (e) {
      setErr(e.message || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token, backend]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshLive = async () => {
    setPickerLoading(true);
    try {
      const r = await fetch(`${backend}/api/admin/printful-products`, { headers: { "X-Admin-Token": token } });
      if (r.ok) { const d = await r.json(); setAllProducts(d.products || []); }
    } finally { setPickerLoading(false); }
  };

  const patch = async (tagName, body) => {
    try {
      const r = await fetch(`${backend}/api/admin/product-tags/${tagName}`, {
        method: "PUT",
        headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      await load();
      flash(`Saved ✓`);
    } catch (e) {
      setErr(e.message || "Save failed");
    }
  };

  return (
    <CollapsibleSection
      id="product-tags"
      testId="admin-tags-panel"
      title={<><Tag size={20} weight="bold" className="text-[#FF1B91] inline mr-2" />Product Collections (Tags)</>}
      subtitle="Products tagged here appear in a dedicated section AND their normal category."
      onRefresh={load}
    >
      <div className="space-y-5">
      {err && <p data-testid="admin-tags-err" className="text-red-400 text-sm border border-red-500/40 bg-red-500/10 p-3">{err}</p>}
      {msg && <p data-testid="admin-tags-msg" className="text-emerald-400 text-sm border border-emerald-500/40 bg-emerald-500/10 p-3">{msg}</p>}

      {loading ? <p className="text-white/50 italic">Loading tags…</p> : (
        <div className="space-y-4">
          {tags.map((t) => {
            const draft = drafts[t.tag] || {};
            const isEditingCopy = draft.__editing;
            return (
              <details key={t.tag} data-testid={`admin-tag-row-${t.tag}`} className={`border border-white/15 bg-black ${t.hidden ? "opacity-60" : ""}`}>
                <summary className="cursor-pointer p-4 flex items-start gap-3 flex-wrap hover:bg-white/[0.02]">
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF1B91]">/ Tag · {t.tag}</p>
                    <p className="font-display font-black uppercase tracking-tight text-lg">{t.label}</p>
                    <p className="text-white/60 text-xs mt-1">{t.product_ids.length} products · {t.hidden ? "hidden" : "visible"} · click to expand</p>
                  </div>
                  <button type="button" onClick={(e) => { e.preventDefault(); patch(t.tag, { hidden: !t.hidden }); }}
                    data-testid={`admin-tag-toggle-hidden-${t.tag}`}
                    className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border ${t.hidden ? "border-red-500 text-red-400" : "border-white/20 text-white/70 hover:border-[#FF1B91]"}`}>
                    {t.hidden ? <><EyeSlash size={12} weight="bold" /> Hidden</> : <><Eye size={12} weight="bold" /> Visible</>}
                  </button>
                  <button type="button" onClick={(e) => { e.preventDefault(); setEditing(t.tag); }}
                    data-testid={`admin-tag-edit-products-${t.tag}`}
                    className="inline-flex items-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em]">
                    <PencilSimple size={12} weight="bold" /> Edit products ({t.product_ids.length})
                  </button>
                  <button type="button"
                    onClick={(e) => { e.preventDefault(); setDrafts((d) => ({ ...d, [t.tag]: { label: t.label, heading: t.heading, description: t.description || "", __editing: !isEditingCopy } })); }}
                    className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border border-white/15">
                    {isEditingCopy ? "Cancel" : "Edit copy"}
                  </button>
                </summary>
                <div className="px-4 pb-4 space-y-3">

                {isEditingCopy && (
                  <div className="border-t border-white/10 pt-3 space-y-2">
                    <input value={draft.label || ""} onChange={(e) => setDrafts((d) => ({ ...d, [t.tag]: { ...d[t.tag], label: e.target.value } }))}
                      placeholder="Label (chip text)" className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm" />
                    <input value={draft.heading || ""} onChange={(e) => setDrafts((d) => ({ ...d, [t.tag]: { ...d[t.tag], heading: e.target.value } }))}
                      placeholder="Heading (section title)" className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm" />
                    <textarea rows={2} value={draft.description || ""} onChange={(e) => setDrafts((d) => ({ ...d, [t.tag]: { ...d[t.tag], description: e.target.value } }))}
                      placeholder="Description (below heading)" className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm" />
                    <button type="button" onClick={() => { patch(t.tag, { label: draft.label, heading: draft.heading, description: draft.description }); setDrafts((d) => ({ ...d, [t.tag]: { ...d[t.tag], __editing: false } })); }}
                      className="inline-flex items-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em]">
                      <FloppyDisk size={12} weight="bold" /> Save copy
                    </button>
                  </div>
                )}

                {/* Thumbnail strip of currently-selected products */}
                <div className="flex flex-wrap gap-2">
                  {t.product_ids.map((pid) => {
                    const prod = allProducts.find((p) => Number(p.id) === Number(pid));
                    const img = prod?.thumbnail_url || "";
                    return (
                      <div key={pid} data-testid={`admin-tag-thumb-${t.tag}-${pid}`} className="w-14 h-14 border border-white/20 bg-black overflow-hidden" title={prod?.name || `#${pid}`}>
                        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <span className="text-[8px] text-white/40 p-1 leading-tight block">#{pid}</span>}
                      </div>
                    );
                  })}
                  {t.product_ids.length === 0 && <span className="text-white/40 text-xs italic">— No products in this collection yet —</span>}
                </div>
                </div>
              </details>
            );
          })}
        </div>
      )}

      {editing && (
        <TagProductPicker
          tag={tags.find((t) => t.tag === editing)}
          allProducts={allProducts}
          loading={pickerLoading}
          onRefresh={refreshLive}
          onCancel={() => setEditing(null)}
          onSave={(ids) => { patch(editing, { product_ids: ids }); setEditing(null); }}
        />
      )}
      </div>
    </CollapsibleSection>
  );
}

function TagProductPicker({ tag, allProducts, loading, onRefresh, onCancel, onSave }) {
  const [selected, setSelected] = useState(new Set((tag?.product_ids || []).map(Number)));
  const [q, setQ] = useState("");

  const toggle = (pid) => {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setSelected(next);
  };

  const filtered = allProducts.filter((p) => !q || (p.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-tag-product-picker" role="dialog" className="fixed inset-0 z-[80] flex items-stretch md:items-center md:justify-center">
      <button type="button" aria-label="Close picker" onClick={onCancel} className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="relative w-full md:max-w-4xl md:max-h-[85vh] h-full md:h-auto bg-[#0A0A0A] border-2 border-[#FF1B91] overflow-y-auto shadow-2xl flex flex-col">
        <header className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 p-4 flex items-center gap-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF1B91]">/ {tag?.label || "Tag"} · pick products</p>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search products…"
            className="flex-1 min-w-[200px] bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
            data-testid="admin-tag-picker-search" />
          <span className="text-white/70 text-xs">{allProducts.length} synced · {selected.size} selected</span>
          <button type="button" onClick={onRefresh} disabled={loading} data-testid="admin-tag-picker-refresh"
            className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 border border-white/20 hover:border-[#FF1B91] disabled:opacity-40">
            <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
            {loading ? "Syncing…" : "Refresh"}
          </button>
          <button type="button" onClick={onCancel} data-testid="admin-tag-picker-cancel"
            className="text-white/60 hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 border border-white/15">
            <Prohibit size={12} weight="bold" className="inline mr-1" /> Cancel
          </button>
          <button type="button" onClick={() => onSave(Array.from(selected))} data-testid="admin-tag-picker-save"
            className="bg-[#FF1B91] hover:bg-[#FF3FA3] text-white text-[10px] font-bold uppercase tracking-[0.25em] px-4 py-2">
            <Check size={12} weight="bold" className="inline mr-1" /> Save selection
          </button>
        </header>
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((p) => {
            const isOn = selected.has(Number(p.id));
            return (
              <button key={p.id} type="button" onClick={() => toggle(Number(p.id))} data-testid={`admin-tag-picker-toggle-${p.id}`}
                className={`text-left border-2 bg-[#0F0F0F] overflow-hidden transition-colors ${isOn ? "border-[#FF1B91] shadow-[0_0_0_2px_#FF1B91]" : "border-white/15 hover:border-[#FF1B91]/60"}`}>
                <div className="aspect-square bg-[#141414] overflow-hidden relative">
                  {p.thumbnail_url ? <img src={p.thumbnail_url} alt={p.name} loading="lazy" className="w-full h-full object-cover" /> : null}
                  {isOn && <span className="absolute top-1 right-1 bg-[#FF1B91] text-white text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5">✓ Picked</span>}
                </div>
                <div className="p-2">
                  <p className="text-white text-[11px] font-bold leading-tight line-clamp-2 min-h-[2.5rem]">{p.name}</p>
                  {p.price_from != null && <p className="text-white/50 text-[10px] mt-0.5">${Math.round(p.price_from)}{p.price_to && p.price_to !== p.price_from ? `–$${Math.round(p.price_to)}` : ""}</p>}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && <p className="col-span-full text-white/40 italic text-sm">No products match.</p>}
        </div>
      </div>
    </div>
  );
}
